// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Modelindstillinger as state: what the Ground draws and how (lag, punktsky,
 * visning, the Klippeboks tool), plus the facts the Ground learns about the project
 * and publishes for the panel (meshes, splats, bounds, …). The Ground reads
 * the state, the gear panel (`ModelSettings`) edits it; both meet in
 * `SettingsContext`. Pure: no React, no DOM.
 */

import type { CloudInfo, GsplatInfo, MeshInfo, PipelineLogEntry, SolidSpace, StageInfo } from '../../../api/types';
import type { Storey } from '../../../bygning/storeys';
import { epochMs } from '../../../pipeline/history';
import { DEFAULT_LIGHTING, type CameraProjection, type LightingState, type ViewPreset } from '../../../viewport/cameraViews';
import type { ElementTally } from '../../../viewport/elementFilter';
import { INITIAL_SERIALIZED_ELEMENT_FILTER, type SerializedElementFilter, type StoreyFilterState } from '../../../viewport/elementFilter';
import { DEFAULT_OVERVIEW_POINTS, MAX_PAGE_SIZE } from '../../../viewport/pagination';

export type Density = 25 | 50 | 100;
export type SizeKey = 'S' | 'M' | 'L';
export type GhostKey = 'svag' | 'normal' | 'staerk';
export type RenderMode = 'points' | 'splat';
/**
 * Farver: the mode's own colouring (the lens, review status, …), the scan's
 * RGB, or one `Label` cloud's ids (the old viewport's label mode).
 */
export type ColourKey = 'lens' | 'rgb' | 'label';

export interface Corner {
  x: number;
  y: number;
  z: number;
}

/** The clipping box, scene-local (the frame `ClippingBoxLayer` works in). */
export interface ClipBox {
  min: Corner;
  max: Corner;
}

export interface ModelSettingsState {
  /** Panorama capture positions in the model. */
  panoramas: boolean;
  posegraph: boolean;
  roomLabels: boolean;
  /**
   * The scan's own reconstruction mesh (`rux create mesh`), when it has one.
   * A debug layer only (Ekspert): the building model's elements are the 3D
   * model (frontend-r22.27).
   */
  mesh: boolean;
  /**
   * Per imported-model category (`imported/<source>/<slug>`, by mesh name)
   * on/off. A category with no entry follows the default for the view
   * (`bygning/modelLayers.ts`: the plan hides the fittings, ceilings, roofs).
   */
  modelLayers: Record<string, boolean>;
  /**
   * Låst plan (frontend-ayw.1): the camera is locked to an orthographic view
   * from straight above — drag pans, the wheel zooms, no orbit. Off puts the
   * camera back where it was.
   */
  planLock: boolean;
  render: RenderMode;
  density: Density;
  size: SizeKey;
  ghost: GhostKey;
  /**
   * Gennemsigtighed for umærkede punkter: how see-through ghost points are,
   * 0 (opaque) .. `GHOST_TRANSPARENCY.max`. Labelled points behind them show.
   */
  ghostTransparency: number;
  /** null = no clipping (hele bygningen). */
  clip: ClipBox | null;
  /**
   * Klippeboks (the Ground's toolbar): the box and its face handles are
   * shown in the model and can be dragged. Independent of the cut itself.
   */
  clipTool: boolean;
  /**
   * Klip: the box cuts the model away outside it. Only has an effect once a
   * box exists (`clip`, or a handle being dragged); off shows the whole
   * model while the box stays where it is.
   */
  clipOn: boolean;
  /** Bumped by "Centrér modellen": the Ground re-frames on a change. */
  frameNonce: number;
  /**
   * A one-shot "frame this box" (world coordinates, metres) — Rediger ›
   * Geometri zooming to an element or a warning; the Ground frames it when
   * `nonce` changes. Never stored.
   */
  focusBox: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; nonce: number } | null;
  /**
   * Per point cloud on/off, by name. A cloud with no entry follows the
   * default: the primary (`cloud`) on, every other one off.
   */
  clouds: Record<string, boolean>;
  colour: ColourKey;
  /** The `Label` cloud Farver › Etiket colours by; null = the first offered. */
  labelSource: string | null;
  /** Point size in metres (world-sized points); null = the Størrelse preset in pixels. */
  sizeMetres: number | null;
  /** Trådnet: the building elements (and a debug mesh) drawn as their wireframe. */
  wireframe: boolean;
  projection: CameraProjection;
  /** A pending preset view; the nonce re-frames when the same view is asked for twice. */
  view: { preset: ViewPreset; nonce: number } | null;
  lighting: LightingState;
  /** Vælg punkt: the next click on the model picks a point for its source images. */
  picking: boolean;
  /**
   * Vis omgivelser: under a room focus, the rest of the building stays as a
   * faint ghost behind the room. Off (default): it is not drawn at all. A
   * storey focus always ghosts.
   */
  showOutside: boolean;
  /**
   * Bygningsdele (frontend-t02): per-category visibility (walls, slabs,
   * roof, spaces, doors, windows, other openings) and the storey filter, for
   * the detected building model (`GET /walls`, `/openings`, `/spaces`, and
   * `solid_slabs`' element index) and, through `elementCategoryForImportedKind`,
   * an imported Revit/IFC model's matching categories alike.
   */
  buildingElements: SerializedElementFilter;
}

export const DEFAULT_SETTINGS: ModelSettingsState = {
  panoramas: true,
  posegraph: false,
  // Off by default (frontend-38f): the names crowd the model; Modelindstillinger turns them on.
  roomLabels: false,
  mesh: false,
  modelLayers: {},
  planLock: false,
  render: 'points',
  density: 100,
  size: 'M',
  ghost: 'normal',
  ghostTransparency: 0.7,
  clip: null,
  clipTool: false,
  clipOn: true,
  frameNonce: 0,
  focusBox: null,
  clouds: {},
  colour: 'lens',
  labelSource: null,
  sizeMetres: null,
  wireframe: false,
  projection: 'perspective',
  view: null,
  lighting: DEFAULT_LIGHTING,
  picking: false,
  showOutside: false,
  buildingElements: INITIAL_SERIALIZED_ELEMENT_FILTER,
};

export type SettingsAction =
  | { type: 'set'; patch: Partial<Omit<ModelSettingsState, 'clip' | 'frameNonce' | 'focusBox' | 'view' | 'clouds' | 'lighting' | 'modelLayers' | 'buildingElements'>> }
  | { type: 'focus'; box: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } }
  | { type: 'modelLayer'; name: string; on: boolean }
  | { type: 'clip'; box: ClipBox | null }
  | { type: 'frame' }
  | { type: 'cloud'; name: string; on: boolean }
  | { type: 'view'; preset: ViewPreset }
  | { type: 'toggle-projection' }
  | { type: 'lighting'; patch: Partial<LightingState> | null }
  | { type: 'buildingElement'; category: string; on: boolean }
  | { type: 'buildingElementAll'; categories: string[]; on: boolean }
  | { type: 'buildingElementStorey'; storey: StoreyFilterState };

export function settingsReducer(state: ModelSettingsState, action: SettingsAction): ModelSettingsState {
  switch (action.type) {
    case 'set':
      return { ...state, ...action.patch };
    case 'clip':
      return { ...state, clip: action.box };
    case 'frame':
      return { ...state, frameNonce: state.frameNonce + 1 };
    case 'focus':
      return { ...state, focusBox: { ...action.box, nonce: (state.focusBox?.nonce ?? 0) + 1 } };
    case 'cloud':
      return { ...state, clouds: { ...state.clouds, [action.name]: action.on } };
    case 'modelLayer':
      return { ...state, modelLayers: { ...state.modelLayers, [action.name]: action.on } };
    case 'view':
      return { ...state, view: { preset: action.preset, nonce: (state.view?.nonce ?? 0) + 1 } };
    case 'toggle-projection':
      return { ...state, projection: state.projection === 'perspective' ? 'orthographic' : 'perspective' };
    case 'buildingElement': {
      const hidden = new Set(state.buildingElements.hidden);
      if (action.on) hidden.delete(action.category);
      else hidden.add(action.category);
      return { ...state, buildingElements: { ...state.buildingElements, hidden: [...hidden] } };
    }
    case 'buildingElementAll':
      return { ...state, buildingElements: { ...state.buildingElements, hidden: action.on ? [] : [...action.categories] } };
    case 'buildingElementStorey':
      return { ...state, buildingElements: { ...state.buildingElements, storey: action.storey } };
    case 'lighting':
      return { ...state, lighting: action.patch === null ? DEFAULT_LIGHTING : { ...state.lighting, ...action.patch } };
  }
}

/** The preset views, in the order Visning lays them out (`rux render --view` names). */
export const VIEW_LABELS: readonly { preset: ViewPreset; label: string }[] = [
  { preset: 'top', label: 'Ovenfra' },
  { preset: 'front', label: 'Forfra' },
  { preset: 'back', label: 'Bagfra' },
  { preset: 'left', label: 'Venstre' },
  { preset: 'right', label: 'Højre' },
  { preset: 'bottom', label: 'Nedefra' },
];

/** Størrelse › Meter: the range the old viewport's point-size slider had. */
export const METRE_SIZE = { min: 0.002, max: 0.12, step: 0.002, initial: 0.02 } as const;

/** Cloud types the Ground can draw. */
export const RENDERABLE_TYPES: ReadonlySet<string> = new Set(['PointXYZRGB', 'PointXYZ']);

/** The primary geometry cloud: `cloud` by name, else the first renderable one. */
export function pickPrimary(clouds: readonly CloudInfo[] | null): CloudInfo | null {
  const renderable = (clouds ?? []).filter((c) => RENDERABLE_TYPES.has(c.type));
  return renderable.find((c) => c.name === 'cloud') ?? renderable[0] ?? null;
}

/** Whether a cloud is drawn: its own switch, else on for the primary only. */
export function cloudOn(state: Pick<ModelSettingsState, 'clouds'>, name: string, primary: string | null): boolean {
  return state.clouds[name] ?? name === primary;
}

/**
 * The `Label` clouds Etiket (the lens switch) can offer for `primary`: those with the
 * same point count. Equal length is necessary, not sufficient, for index
 * alignment, so it only excludes the clouds that cannot be zipped on — a
 * misaligned label cloud would draw a plausible, entirely fictional
 * segmentation.
 */
export function labelSourcesFor(clouds: readonly CloudInfo[] | null, primary: CloudInfo | null): CloudInfo[] {
  if (!primary) return [];
  return (clouds ?? []).filter((c) => c.type === 'Label' && c.point_count === primary.point_count);
}

/** Why Etiket is unavailable: no label clouds at all, or none that fit the primary; null while loading. */
export function labelNoteFor(clouds: readonly CloudInfo[] | null): string | null {
  if (clouds === null) return null;
  return clouds.some((c) => c.type === 'Label')
    ? 'Ingen etiketsky passer punkt for punkt til punktskyen.'
    : 'Ingen etiketskyer i sagen endnu — planes, rooms og instances laver dem.';
}

/** The label source in use: the chosen one while it is still offered, else the first. */
export function activeLabelSource(sources: readonly CloudInfo[], chosen: string | null): CloudInfo | null {
  return sources.find((c) => c.name === chosen) ?? sources[0] ?? null;
}

/**
 * How the primary layer is coloured and which label clouds it zips: the
 * Ground's own (`base`: instances, rooms) plus, for Etiket, the label
 * source — first, since the scene's label mode colours by the first one.
 * Lens without a colour table, and Etiket without a source, fall back to RGB.
 */
export function resolveColouring(
  colour: ColourKey,
  hasLut: boolean,
  labelSource: string | null,
  base: readonly string[],
): { colorMode: 'rgb' | 'label' | 'lut'; labelClouds: string[] } {
  if (colour === 'label' && labelSource) {
    return { colorMode: 'label', labelClouds: [labelSource, ...base.filter((n) => n !== labelSource)] };
  }
  const colorMode = colour !== 'rgb' && hasLut ? 'lut' : 'rgb';
  return { colorMode, labelClouds: [...base] };
}

/** Point size and unit for the scene: metres when set, else the preset in pixels. */
export function pointSizeOf(state: Pick<ModelSettingsState, 'size' | 'sizeMetres'>): { size: number; unit: 'm' | 'px' } {
  return state.sizeMetres !== null ? { size: state.sizeMetres, unit: 'm' } : { size: SIZE_PX[state.size], unit: 'px' };
}

/** Størrelse → point size in screen pixels (the prototype's 0.7 / 1 / 1.5 × M). */
export const SIZE_PX: Record<SizeKey, number> = { S: 1.75, M: 2.5, L: 3.75 };

/** The Gennemsigtighed slider: 0 = opaque ghosts (the old look), at most 95 % see-through. */
export const GHOST_TRANSPARENCY = { min: 0, max: 0.95, step: 0.05 } as const;

/** Ghost points' opacity for the scene from the slider's transparency, clamped to its range. */
export function ghostOpacityOf(transparency: number): number {
  const t = Number.isFinite(transparency) ? transparency : DEFAULT_SETTINGS.ghostTransparency;
  return 1 - Math.min(GHOST_TRANSPARENCY.max, Math.max(GHOST_TRANSPARENCY.min, t));
}

/** Skygge → multiplier on `--ghost-alpha` (the prototype's 0.5 / 1 / 1.8). */
export const GHOST_STRENGTH: Record<GhostKey, number> = { svag: 0.5, normal: 1, staerk: 1.8 };

/**
 * Tæthed → the stream's overview budget. 100 %: the default overview, then
 * every point. Below: one LOD request of that share of the cloud and stop —
 * capped by the endpoint's one-request maximum, so on a very large scan 25 %
 * and 50 % can both come back as that maximum.
 */
export function densityBudget(density: Density, pointCount: number): { overviewPoints: number; lodOnly: boolean } {
  if (density >= 100 || !Number.isFinite(pointCount) || pointCount <= 0) {
    return { overviewPoints: DEFAULT_OVERVIEW_POINTS, lodOnly: false };
  }
  const want = Math.ceil((pointCount * density) / 100);
  return { overviewPoints: Math.max(1, Math.min(MAX_PAGE_SIZE, want)), lodOnly: true };
}

/** `box` with one face moved, keeping min ≤ max (the other face follows, as the prototype's sliders do). */
export function moveFace(box: ClipBox, axis: keyof Corner, side: 'min' | 'max', value: number): ClipBox {
  const min = { ...box.min };
  const max = { ...box.max };
  if (side === 'min') {
    min[axis] = value;
    if (max[axis] < value) max[axis] = value;
  } else {
    max[axis] = value;
    if (min[axis] > value) min[axis] = value;
  }
  return { min, max };
}

/**
 * The clipping box for a storey preset: z (height) cut to the storey's
 * `zMin`..`zMax`, x and y back to the whole model. The storey's heights are
 * world coordinates (the instance index's frame); the box is scene-local, so
 * the scene's world origin is subtracted, then the range is clamped to the
 * model's bounds (a storey that runs past the model's top or bottom stops
 * there). Null when the storey misses the model entirely.
 */
export function storeyClip(bounds: ClipBox, storey: Pick<Storey, 'zMin' | 'zMax'>, origin: Corner): ClipBox | null {
  const lo = Math.max(bounds.min.z, storey.zMin - origin.z);
  const hi = Math.min(bounds.max.z, storey.zMax - origin.z);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) return null;
  return { min: { ...bounds.min, z: lo }, max: { ...bounds.max, z: hi } };
}

/** Whether two boxes are the same to within `eps` (a slider round-trip is not exact). */
export function sameBox(a: ClipBox | null, b: ClipBox | null, eps = 1e-6): boolean {
  if (a === null || b === null) return a === b;
  const axes: (keyof Corner)[] = ['x', 'y', 'z'];
  return axes.every((k) => Math.abs(a.min[k] - b.min[k]) <= eps && Math.abs(a.max[k] - b.max[k]) <= eps);
}

/** One line of the panel's pipeline list. */
export interface PipelineRow {
  stage: string;
  /** The latest run's status, or null when the stage never ran. */
  status: PipelineLogEntry['status'] | null;
  /** `DD.MM HH:MM` of the latest run's start (the stored UTC stamp, unshifted), or null. */
  when: string | null;
}

/**
 * The catalogue's stages with their latest run from the log (matched on
 * `log_name`, else the stage token), in catalogue order. Stages with no runner
 * and no history are left out — there is nothing to say about them.
 */
export function pipelineRows(stages: readonly StageInfo[], entries: readonly PipelineLogEntry[]): PipelineRow[] {
  const latest = new Map<string, PipelineLogEntry>();
  for (const e of entries) {
    const prev = latest.get(e.stage);
    if (!prev || (epochMs(e.started_at) ?? 0) > (epochMs(prev.started_at) ?? 0)) latest.set(e.stage, e);
  }
  const rows: PipelineRow[] = [];
  for (const s of stages) {
    const run = latest.get(s.log_name) ?? latest.get(s.stage) ?? null;
    if (!run && !s.runnable) continue;
    rows.push({ stage: s.stage, status: run?.status ?? null, when: run ? shortStamp(run.started_at) : null });
  }
  return rows;
}

function shortStamp(stored: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(stored);
  return m ? `${m[3]}.${m[2]} ${m[4]}:${m[5]}` : stored;
}

/** What the Ground knows about the project, for the panel to show. */
export interface GroundFacts {
  /** Every cloud in the project (`/clouds`); null while loading. */
  clouds: CloudInfo[] | null;
  /** The primary geometry cloud's name (the one coloured and clicked); null without one. */
  primary: string | null;
  meshes: MeshInfo[] | null;
  gsplats: GsplatInfo[] | null;
  /** Panoramas that can be placed in the model; null while loading. */
  panoramas: number | null;
  /** Pose-graph nodes; null until the graph was fetched (on first switch-on). */
  poseNodes: number | null;
  /** The building model's walls and openings (`GET /walls`, `/openings`); null while loading or unknown. */
  walls: { walls: number; openings: number } | null;
  /** Scene-local bounds of the loaded model; null before content. */
  bounds: ClipBox | null;
  /** Instances with points in the loaded model. */
  instances: number;
  /** The splat's download: null idle, 0..1 loading, 1 loaded. */
  splatProgress: number | null;
  splatError: string | null;
  meshLoading: boolean;
  meshError: string | null;
  /** World coordinates of scene 0,0,0 (the recentring origin); null before content. */
  worldOrigin: Corner | null;
  /**
   * The storeys the Ground publishes (world z): the stored ones from
   * `GET /storeys`, else derived from the room centroids (gap 7's
   * heuristic). Empty before either is known, without rooms, or when the
   * building has a single storey.
   */
  storeys: Storey[];
  /** `GET /spaces` (the building model's rooms, frontend-2ay); null before read, or on a server without the route. */
  spaces: SolidSpace[] | null;
  /**
   * Bygningsdele (frontend-t02): walls, openings (by door/window/other),
   * spaces and slab/roof (from `solid_slabs`' element index) as one tally
   * per category and storey, for the panel's rows and counts. Null while
   * loading, or `[]` once read with nothing in any of the four sources.
   */
  buildingElementTallies: ElementTally[] | null;
}

export const EMPTY_FACTS: GroundFacts = {
  clouds: null,
  primary: null,
  meshes: null,
  gsplats: null,
  panoramas: null,
  poseNodes: null,
  walls: null,
  bounds: null,
  instances: 0,
  splatProgress: null,
  splatError: null,
  meshLoading: false,
  meshError: null,
  worldOrigin: null,
  storeys: [],
  spaces: null,
  buildingElementTallies: null,
};

/** `facts` with `patch` merged, or `facts` itself when nothing changed (no re-render). */
export function mergeFacts(facts: GroundFacts, patch: Partial<GroundFacts>): GroundFacts {
  let changed = false;
  for (const key of Object.keys(patch) as (keyof GroundFacts)[]) {
    if (!sameValue(facts[key], patch[key])) {
      changed = true;
      break;
    }
  }
  return changed ? { ...facts, ...patch } : facts;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Storeys are re-derived on every index revision; compare by value (a
  // handful of small records, so JSON is fine).
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && JSON.stringify(a) === JSON.stringify(b);
  // The world origin, likewise.
  if (a && b && typeof a === 'object' && typeof b === 'object' && 'x' in a && 'x' in b) {
    const x = a as Corner;
    const y = b as Corner;
    return x.x === y.x && x.y === y.y && x.z === y.z;
  }
  // Bounds arrive as fresh objects on every index revision; compare by value.
  if (a && b && typeof a === 'object' && typeof b === 'object' && 'min' in a && 'min' in b) {
    const x = a as ClipBox;
    const y = b as ClipBox;
    return (
      x.min.x === y.min.x &&
      x.min.y === y.min.y &&
      x.min.z === y.min.z &&
      x.max.x === y.max.x &&
      x.max.y === y.max.y &&
      x.max.z === y.max.z
    );
  }
  return false;
}
